import { createHash } from "node:crypto";
import { parseAgentControllerRef } from "@wakeloop/agent-runtime";
import {
  buildDispatchReplyContextSummary,
  SPACE_ACTION_CONTEXT_GUIDANCE_LINES,
} from "@wakeloop/local-service";
import {
  collectCanonicalMentionTextFragments,
  resolveSingleSpaceMentionLabels,
} from "@wakeloop/schemas";
import type {
  RuntimeCoordinatorExecutionTokenEcho,
  RuntimeTransportDeliverDispatchPayload,
  RuntimeTransportDeliverDispatchRequest,
  SpaceContract,
  WakeLoopDaemonDispatchContinuitySnapshot,
  WakeLoopDaemonDispatchDeliveryRecoveryReason,
  WakeLoopDaemonDispatchReplyToSnapshot,
} from "@wakeloop/schemas/types";
import {
  buildAgentFacingMentalModelLines,
  buildCenterAgentRoleTemplateText,
  composeDispatchBrief,
  composeEntryBrief,
  deriveContinuity,
  resolveSpacePublicationRequirement,
} from "@wakeloop/space-mesh";
import { createDefaultRuntimeAgentControllerSettings } from "../../config/runtime-config";
import {
  isLocalSpaceStateCorruptResult,
  readSpaceConfig,
  toLocalSpaceStateIntegrityError,
} from "../../space/local-state";
import {
  projectSpaceFacts,
  projectSpaceProfileCache,
} from "../../space/local-state-projection";
import { BOOTSTRAP_INJECTION_KEY_SEPARATOR } from "../core/constants";
import {
  normalizeNonNegativeInteger,
  normalizeOptionalText,
  normalizeText,
} from "../core/logger";
import type {
  DispatchCorrelation,
  DispatchPromptBootstrapInput,
  DispatchTaskParseInput,
  ParsedDispatchTask,
} from "../core/types";

const WAKELOOP_SPACE_DEFINITION = buildAgentFacingMentalModelLines()
  .slice(0, 2)
  .join(" ");
const ENTRY_BRIEF_TEMPLATE_VERSION = "space-mesh-entry-brief-v1" as const;
export const COMPACT_DISPATCH_BOOTSTRAP_TEMPLATE_VERSION =
  ENTRY_BRIEF_TEMPLATE_VERSION;
const DEFAULT_SPACE_PURPOSE = "shared coordination";

type DispatchTaskRequiredFields = {
  attemptId: string;
  dispatchId: string;
  sourceEventId: string;
  spaceId: string;
  targetProfileId: string;
  taskId: string;
};

type DispatchTaskControllerSelection = {
  agentControllerRef: string;
};

export function parseDispatchTask(
  input: DispatchTaskParseInput
): ParsedDispatchTask | null {
  const payload = resolveDispatchTaskPayload(input.deliverRequest);

  const requiredFields = resolveDispatchTaskRequiredFields({
    deliverRequest: input.deliverRequest,
    payload,
  });
  if (!requiredFields) {
    return null;
  }

  const mentionSourceProfileId =
    normalizeOptionalText(payload.sourceSignal.profileId) ??
    normalizeOptionalText(payload.sourceSignalSnapshot?.profileId);
  const mentionSourceProfileKind =
    payload.sourceSignal.profileKind ??
    payload.sourceSignalSnapshot?.profileKind ??
    null;
  const mentionSourceProfileMentionLabel =
    normalizeOptionalText(payload.sourceSignal.profileMentionLabel) ??
    normalizeOptionalText(payload.sourceSignalSnapshot?.profileMentionLabel);
  const rawPrompt =
    normalizeOptionalText(payload.sourceSignal.text) ??
    normalizeOptionalText(payload.sourceSignalSnapshot?.text);
  const sourceMessageEnvelope = payload.sourceMessageEnvelope ?? null;
  const prompt = resolveDispatchPrompt({
    rawPrompt,
    sourceMessageEnvelope,
  });
  const targetProfileDisplay =
    resolveDispatchTaskTargetProfileDisplayFacts(payload);
  const spaceContext = resolveDispatchTaskSpaceContext(payload);
  const triggerMode =
    spaceContext.spaceContractSnapshot?.dispatchPolicy.mode ??
    payload.dispatchPolicy;
  const recipientExplicitlyAddressedByActivationTargets =
    isRecipientExplicitlyAddressedByActivationTargets({
      sourceMessageEnvelope,
      targetProfileId: requiredFields.targetProfileId,
    });
  const sequence =
    normalizeNonNegativeInteger(payload.enqueuedAtMs) ?? Date.now();

  const controllerSelection = resolveDispatchTaskControllerSelection({
    fallbackAgentControllerRef:
      input.fallbackRouteConfig?.agentControllerRef ?? null,
    payload,
  });
  if (!(prompt && controllerSelection)) {
    return null;
  }

  const fallbackControllerSettings =
    createDefaultRuntimeAgentControllerSettings(
      controllerSelection.agentControllerRef
    );
  const agentControllerConversationId =
    normalizeOptionalText(payload.agentControllerConversationId) ?? null;
  const deliveryRecoveryReason = resolveDeliveryRecoveryReason(payload);
  const executionTokenEcho = resolveExecutionTokenEcho(payload);
  if (!executionTokenEcho) {
    return null;
  }
  const transportMode =
    payload.transportMode ??
    input.fallbackRouteConfig?.transportMode ??
    fallbackControllerSettings.transportMode;
  const streamingMode =
    payload.streamingMode ??
    input.fallbackRouteConfig?.streamingMode ??
    fallbackControllerSettings.streamingMode;
  const contextIdMappingSpec =
    payload.contextIdMappingSpec ??
    input.fallbackRouteConfig?.contextIdMappingSpec ??
    fallbackControllerSettings.contextIdMappingSpec;

  return {
    ...requiredFields,
    agentControllerConversationId,
    attachments: payload.sourceSignalSnapshot?.attachments ?? [],
    ...controllerSelection,
    deliveryRecoveryReason,
    defaultProjectId: normalizeOptionalText(payload.defaultProjectId) ?? null,
    executionIdentity: payload.executionIdentity,
    executionTokenEcho,
    mentionSourceProfileId,
    mentionSourceProfileKind,
    mentionSourceProfileMentionLabel: mentionSourceProfileMentionLabel ?? null,
    prompt,
    targetProfileIsCenterAgent: payload.targetProfileIsCenterAgent === true,
    replyToSnapshot: spaceContext.replyToSnapshot,
    recipientExplicitlyAddressedByActivationTargets,
    runtimeCoordinatorExecutionId: payload.runtimeCoordinatorExecutionId,
    sequence,
    dispatchTriggerSource: resolveDispatchTriggerSource({
      recipientExplicitlyAddressedByActivationTargets,
      triggerMode,
    }),
    spaceContractSnapshot: spaceContext.spaceContractSnapshot,
    spaceNameSnapshot: spaceContext.spaceNameSnapshot,
    spacePassword: normalizeOptionalText(payload.spacePassword) ?? null,
    sourceMessageEnvelope,
    threadContext: spaceContext.threadContext,
    streamingMode,
    continuitySnapshot: spaceContext.continuitySnapshot,
    transportMode,
    ...targetProfileDisplay,
    triggerMode,
    contextIdMappingSpec,
  };
}

function resolveDispatchTaskTargetProfileDisplayFacts(
  payload: RuntimeTransportDeliverDispatchPayload
): Pick<ParsedDispatchTask, "targetProfileMentionLabel" | "targetProfileName"> {
  return {
    targetProfileMentionLabel:
      normalizeOptionalText(payload.targetProfileMentionLabel) ?? null,
    targetProfileName: normalizeOptionalText(payload.targetProfileName) ?? null,
  };
}

function resolveDispatchPrompt(input: {
  rawPrompt: string | null;
  sourceMessageEnvelope: ParsedDispatchTask["sourceMessageEnvelope"];
}): string | null {
  const prompt = normalizeOptionalText(input.rawPrompt);
  if (!prompt) {
    return null;
  }
  if ((input.sourceMessageEnvelope?.activationTargets.length ?? 0) === 0) {
    return prompt;
  }

  const wakeMentionFragments = collectCanonicalMentionTextFragments(
    prompt
  ).filter((fragment) => fragment.parsedLiteral?.type === "wake");
  if (wakeMentionFragments.length === 0) {
    return prompt;
  }

  let cursor = 0;
  const parts: string[] = [];
  for (const fragment of wakeMentionFragments) {
    parts.push(prompt.slice(cursor, fragment.start));
    cursor = fragment.end;
  }
  parts.push(prompt.slice(cursor));

  return normalizeOptionalText(parts.join(" ").replace(/\s+/gu, " ")) ?? prompt;
}

function resolveDispatchTaskSpaceContext(
  payload: RuntimeTransportDeliverDispatchPayload
): {
  continuitySnapshot: WakeLoopDaemonDispatchContinuitySnapshot | null;
  replyToSnapshot: WakeLoopDaemonDispatchReplyToSnapshot | null;
  spaceContractSnapshot: SpaceContract | null;
  spaceNameSnapshot: string | null;
  threadContext: ParsedDispatchTask["threadContext"];
} {
  return {
    continuitySnapshot: payload.continuitySnapshot ?? null,
    replyToSnapshot: payload.replyToSnapshot ?? null,
    spaceContractSnapshot: payload.spaceContractSnapshot ?? null,
    spaceNameSnapshot: normalizeOptionalText(payload.spaceNameSnapshot) ?? null,
    threadContext: payload.threadContext ?? null,
  };
}

function resolveDispatchTriggerSource(input: {
  recipientExplicitlyAddressedByActivationTargets: boolean;
  triggerMode: ParsedDispatchTask["triggerMode"];
}): ParsedDispatchTask["dispatchTriggerSource"] {
  if (input.recipientExplicitlyAddressedByActivationTargets) {
    return "activation_targets";
  }
  if (input.triggerMode === "listen_all") {
    return "listen_all";
  }
  return "activation_targets";
}

function isRecipientExplicitlyAddressedByActivationTargets(input: {
  sourceMessageEnvelope: ParsedDispatchTask["sourceMessageEnvelope"];
  targetProfileId: string;
}): boolean {
  const normalizedTargetProfileId = normalizeText(input.targetProfileId);
  if (!normalizedTargetProfileId) {
    return false;
  }
  return (
    input.sourceMessageEnvelope?.activationTargets.includes(
      normalizedTargetProfileId
    ) ?? false
  );
}

function resolveDeliveryRecoveryReason(
  payload: RuntimeTransportDeliverDispatchPayload
): WakeLoopDaemonDispatchDeliveryRecoveryReason | null {
  const normalized = normalizeOptionalText(payload.deliveryRecoveryReason);
  if (normalized === "connection_replaced" || normalized === "route_lost") {
    return normalized;
  }
  return null;
}

function resolveExecutionTokenEcho(
  payload: RuntimeTransportDeliverDispatchPayload
): RuntimeCoordinatorExecutionTokenEcho | null {
  const executionToken = payload.executionToken;
  if (!executionToken) {
    return null;
  }
  return {
    executionToken: executionToken.executionToken,
    runId: executionToken.runId,
    runtimeCoordinatorExecutionId:
      executionToken.executionScope.runtimeCoordinatorExecutionId,
    tokenFingerprint: executionToken.tokenFingerprint,
  };
}

function resolveDispatchTaskPayload(
  request: RuntimeTransportDeliverDispatchRequest
) {
  return request.payload;
}

function resolveDispatchTaskRequiredFields(input: {
  deliverRequest: RuntimeTransportDeliverDispatchRequest;
  payload: RuntimeTransportDeliverDispatchPayload;
}): DispatchTaskRequiredFields | null {
  const dispatchId =
    normalizeOptionalText(input.payload.dispatchId) ??
    input.deliverRequest.dispatchId;
  const attemptId =
    normalizeOptionalText(input.payload.attemptId) ??
    normalizeOptionalText(input.deliverRequest.attemptId);
  const taskId = normalizeOptionalText(input.payload.taskId);
  const sourceEventId = normalizeOptionalText(input.payload.sourceEventId);
  const spaceId = normalizeOptionalText(input.payload.spaceId);
  const targetProfileId =
    normalizeOptionalText(input.payload.targetProfileId) ??
    normalizeOptionalText(input.deliverRequest.profileId);
  if (
    !(
      attemptId &&
      dispatchId &&
      taskId &&
      sourceEventId &&
      spaceId &&
      targetProfileId
    )
  ) {
    return null;
  }
  return {
    attemptId,
    dispatchId,
    sourceEventId,
    spaceId,
    targetProfileId,
    taskId,
  };
}

function resolveDispatchTaskControllerSelection(input: {
  fallbackAgentControllerRef: string | null;
  payload: RuntimeTransportDeliverDispatchPayload;
}): DispatchTaskControllerSelection | null {
  const agentControllerRef =
    normalizeOptionalText(
      input.payload.controllerSnapshot.agentControllerRef
    ) ?? input.fallbackAgentControllerRef;
  const parsedAgentControllerRef = parseAgentControllerRef(agentControllerRef);
  if (!parsedAgentControllerRef) {
    return null;
  }
  return {
    agentControllerRef: parsedAgentControllerRef.ref,
  };
}

export function buildDispatchPromptWithBootstrap(
  input: DispatchPromptBootstrapInput
): string {
  const prompt = normalizeText(input.prompt);
  if (!prompt) {
    return "";
  }

  const sections: string[] = [];
  if (input.shouldInjectBootstrap && isEntryBriefEnabled(input)) {
    sections.push(renderEntryBriefSection(input));
  }
  if ((input.spaceContractSnapshot?.dispatchBrief ?? "default") !== "none") {
    sections.push(renderDispatchBriefSection(input));
  }
  const centerAgentRoleSection = renderCenterAgentRoleSection(input);
  if (centerAgentRoleSection) {
    sections.push(centerAgentRoleSection);
  }
  sections.push(["Current Wake message:", prompt].join("\n"));
  if (input.imageAttachmentPromptSection) {
    sections.push(input.imageAttachmentPromptSection);
  }
  const completionSection = renderMandatoryCompletionSection(input);
  if (completionSection) {
    sections.push(completionSection);
  }
  return sections.filter(Boolean).join("\n\n");
}

function renderCenterAgentRoleSection(
  input: DispatchPromptBootstrapInput
): string | null {
  if (input.shouldInjectCenterAgentRoleTemplate !== true) {
    return null;
  }
  return ["Center Agent Role:", buildCenterAgentRoleTemplateText()].join("\n");
}

export function buildDispatchBootstrapTemplateFacts(
  input: Omit<DispatchPromptBootstrapInput, "prompt" | "shouldInjectBootstrap">
): {
  contentFingerprint: string;
  templateVersion: typeof COMPACT_DISPATCH_BOOTSTRAP_TEMPLATE_VERSION;
} {
  const contentFingerprint = createHash("sha256")
    .update(
      isEntryBriefEnabled(input)
        ? renderEntryBriefSectionText(composeEntryBriefFromInput(input))
        : "",
      "utf8"
    )
    .digest("hex");

  return {
    contentFingerprint,
    templateVersion: COMPACT_DISPATCH_BOOTSTRAP_TEMPLATE_VERSION,
  };
}

export function buildBootstrapInjectionKey(input: {
  spaceId: string;
  targetProfileId: string;
}): string {
  return [normalizeText(input.spaceId), normalizeText(input.targetProfileId)]
    .map((value) => encodeURIComponent(value))
    .join(BOOTSTRAP_INJECTION_KEY_SEPARATOR);
}

function renderEntryBriefSection(input: DispatchPromptBootstrapInput): string {
  return renderEntryBriefSectionText(composeEntryBriefFromInput(input));
}

function isEntryBriefEnabled(input: {
  spaceContractSnapshot?: DispatchPromptBootstrapInput["spaceContractSnapshot"];
}): boolean {
  return (input.spaceContractSnapshot?.entryBrief ?? "default") !== "none";
}

function composeEntryBriefFromInput(
  input: Omit<DispatchPromptBootstrapInput, "prompt" | "shouldInjectBootstrap">
): ReturnType<typeof composeEntryBrief> {
  const targetProfileIdentity = buildTargetProfileIdentityLine({
    targetProfileId: input.targetProfileId,
    targetProfileName: input.targetProfileName,
    targetProfileMentionLabel: input.targetProfileMentionLabel,
    targetProfileRole: input.targetProfileRole,
  });
  const howThisRoomWorks = buildHowThisRoomWorksLine(input);

  return composeEntryBrief({
    whatThisRoomIs: WAKELOOP_SPACE_DEFINITION,
    youAre: targetProfileIdentity,
    thisRoomIsFor: buildThisRoomIsForLine(input),
    howThisRoomWorks,
    ifUnsure: buildIfUnsureLine({
      cliEntry: input.cliEntry,
      spaceId: input.spaceId,
    }),
  });
}

function renderEntryBriefSectionText(
  entryBrief: ReturnType<typeof composeEntryBrief>
): string {
  const lines = [
    "Space Entry Brief:",
    `- What this space is: ${entryBrief.whatThisRoomIs}`,
    `- You are: ${entryBrief.youAre}`,
    entryBrief.thisRoomIsFor === DEFAULT_SPACE_PURPOSE
      ? null
      : `- This space is for: ${entryBrief.thisRoomIsFor}`,
    `- How this space works: ${entryBrief.howThisRoomWorks}`,
    `- If unsure: ${entryBrief.ifUnsure}`,
  ];
  return lines.filter(Boolean).join("\n");
}

function renderDispatchBriefSection(
  input: DispatchPromptBootstrapInput
): string {
  const dispatchBrief = composeDispatchBrief({
    activatedBy: buildActivatedByLine(input),
    sourceMessage: buildSourceMessageLine(input.prompt),
    replyTarget: buildReplyTargetLine({
      replyToSnapshot: input.replyToSnapshot,
      threadContext: input.threadContext ?? null,
    }),
    continuity: deriveContinuity({
      messagesSinceThen: input.continuitySnapshot?.messagesSinceThen ?? 0,
      higherAuthorityUpdateCount: input.continuitySnapshot
        ?.higherAuthorityUpdates
        ? 1
        : 0,
    }),
    whatYouCanDoNow: buildWhatYouCanDoNowLine(input),
  });

  const lines = [
    "Space Dispatch Brief:",
    `- Activated by: ${dispatchBrief.activatedBy}`,
    `- Dispatch trigger: ${buildDispatchTriggerLine(input)}`,
    shouldRenderReplyTargetLine(dispatchBrief.replyTarget)
      ? `- Reply target: ${dispatchBrief.replyTarget}`
      : null,
    renderContinuityLine(dispatchBrief.continuity),
    `- Actions: ${dispatchBrief.whatYouCanDoNow}`,
    ...buildServiceFreshnessWarningLines(input),
    ...buildActionGuidanceLines(input),
    ...buildSendTargetReferenceLines(input),
  ];

  return lines.filter(Boolean).join("\n");
}

function renderMandatoryCompletionSection(
  input: DispatchPromptBootstrapInput
): string | null {
  const publicationRequirement = resolveSpacePublicationRequirement(
    input.spaceContractSnapshot
  );
  if (publicationRequirement.allowsRawReplyWithoutAction) {
    return null;
  }

  return [
    "Mandatory completion:",
    "- Do not finish this Wake with a plain-text final answer. Plain prose stays local and is not a public Space reply in Collaboration mode.",
    buildMandatoryCompletionActionLine(input, publicationRequirement),
    "- After the Space Action command succeeds, stop. WakeLoop reads that command as the public outcome.",
    `- Use fallback JSON only if the Space Action command cannot run: \`${buildPromptSpaceActionCommand(input)}\`.`,
  ].join("\n");
}

function buildMandatoryCompletionActionLine(
  input: Pick<DispatchPromptBootstrapInput, "cliEntry">,
  publicationRequirement: ReturnType<typeof resolveSpacePublicationRequirement>
): string {
  if (publicationRequirement.allowedActions.includes("post_message")) {
    return `- Normal completion: run \`${buildSpaceActionCommand(
      input,
      'reply "your public message"'
    )}\` and wait for it to finish.`;
  }
  if (publicationRequirement.allowedActions.includes("post_status")) {
    return `- Normal completion: run \`${buildSpaceActionCommand(
      input,
      'status done "short completion update"'
    )}\` and wait for it to finish.`;
  }
  if (publicationRequirement.allowedActions.includes("stay_silent")) {
    return `- Normal completion: run \`${buildSpaceActionCommand(
      input,
      'silent --reason "reason"'
    )}\` and wait for it to finish.`;
  }
  return `- Normal completion: run \`${buildPromptSpaceActionCommand(
    input
  )}\` and return one valid trailing wakeloop-space-action block.`;
}

function buildServiceFreshnessWarningLines(
  input: Pick<DispatchPromptBootstrapInput, "serviceFreshnessWarningLines">
): string[] {
  return input.serviceFreshnessWarningLines?.filter(Boolean) ?? [];
}

function buildTargetProfileIdentityLine(input: {
  targetProfileId: string;
  targetProfileName?: string | null;
  targetProfileMentionLabel?: string | null;
  targetProfileRole?: string | null;
}): string {
  const profileName =
    normalizeOptionalText(input.targetProfileName) ??
    normalizeOptionalText(input.targetProfileMentionLabel) ??
    "unknown";
  const role = normalizeOptionalText(input.targetProfileRole);
  const identityParts = [
    profileName,
    `Profile ID: ${normalizeText(input.targetProfileId) || "unknown"}`,
  ];
  if (role) {
    identityParts.push(`Role: ${role}`);
  }
  return identityParts.join(", ");
}

function buildThisRoomIsForLine(
  input: Omit<DispatchPromptBootstrapInput, "prompt" | "shouldInjectBootstrap">
): string {
  const spaceName = normalizeOptionalText(input.spaceName);
  const spacePurpose = normalizeOptionalText(input.spacePurpose);
  if (spaceName && spacePurpose) {
    return `${spaceName}: ${spacePurpose}`;
  }
  if (spacePurpose) {
    return spacePurpose;
  }
  if (spaceName) {
    return spaceName;
  }
  return DEFAULT_SPACE_PURPOSE;
}

function buildHowThisRoomWorksLine(
  input: Omit<DispatchPromptBootstrapInput, "prompt" | "shouldInjectBootstrap">
): string {
  const dispatchMode =
    input.spaceContractSnapshot?.dispatchPolicy.mode ?? "mention_only";
  const publicationRequirement = resolveSpacePublicationRequirement(
    input.spaceContractSnapshot
  );
  const dispatchLine =
    dispatchMode === "listen_all"
      ? "You may be dispatched from public space messages."
      : "You are usually dispatched on mention.";
  return [
    dispatchLine,
    "Wake means you should do work; Reference and CC are context only.",
    publicationRequirement.howThisSpaceWorks,
    "Follow this space's authority order if instructions conflict.",
  ].join(" ");
}

function buildWhatYouCanDoNowLine(
  input: Omit<DispatchPromptBootstrapInput, "prompt" | "shouldInjectBootstrap">
): string {
  return resolveSpacePublicationRequirement(
    input.spaceContractSnapshot
  ).allowedActions.join(" | ");
}

function buildActionGuidanceLines(
  input: Omit<DispatchPromptBootstrapInput, "prompt" | "shouldInjectBootstrap">
): string[] {
  const publicationRequirement = resolveSpacePublicationRequirement(
    input.spaceContractSnapshot
  );
  const lines: string[] = [];
  if (publicationRequirement.allowsRawReplyWithoutAction) {
    lines.push("- Default: share your raw reply to the Space.");
  } else {
    lines.push(
      "- Keep private work, progress narration, tool logs, and drafts in your local agent context unless the user explicitly asked to publish them."
    );
  }
  lines.push(buildPrimaryFinalActionLine(input, publicationRequirement));
  lines.push(
    ...buildConditionalSpaceActionCommandLines(input, publicationRequirement)
  );
  lines.push(
    `- Need context? Run \`${buildSpaceHistoryCommand({
      cliEntry: input.cliEntry,
      spaceId: input.spaceId,
    })}\`.`
  );
  if (input.threadContext) {
    lines.push(
      "- This dispatch belongs to a thread. Public replies should stay in that thread unless explicitly asked to reply in the main Space."
    );
  }
  lines.push(...SPACE_ACTION_CONTEXT_GUIDANCE_LINES);
  lines.push(
    "- If the current message mentions multiple targets, handle only the part assigned to you unless it explicitly asks you to synthesize for the group."
  );
  lines.push(
    "- Wake asks another Agent Profile to act. Reference and CC are context only. Bare @names in prose do not create executable work.",
    "- For a normal reply to this Wake, do not add target flags. Use target flags only when this Wake asks you to hand off, reference, or CC another profile."
  );
  // Auto-relay chains have no transport-level loop limit (see "Auto-Relay
  // Loop Policy" in developing/architecture/agent-transport-contract.md), so
  // loop discipline lives in this static guidance for now. Future (dynamic
  // prompt injection design): render escalating loop-awareness reminders
  // here from chain telemetry (autoRelayDepth thresholds, visited profiles)
  // instead of a single static line.
  lines.push(
    "- Never wake another Agent Profile just to thank, acknowledge, or confirm receipt. Wake a profile only when you need work from it that moves the goal forward. When the goal is reached or you have nothing new to add, end with a reply that wakes no one."
  );
  lines.push(
    `- Need action details? Run \`${buildSpaceActionCommand(
      input,
      "help"
    )}\`. Only if every Space Action command fails, use \`${buildPromptSpaceActionCommand(input)}\` for fallback JSON.`
  );
  return lines;
}

function buildPrimaryFinalActionLine(
  input: Pick<DispatchPromptBootstrapInput, "cliEntry">,
  publicationRequirement: ReturnType<typeof resolveSpacePublicationRequirement>
): string {
  if (publicationRequirement.allowsRawReplyWithoutAction) {
    return "- Final action for this Wake: write the public reply normally.";
  }
  if (publicationRequirement.allowedActions.includes("post_message")) {
    return `- Final action for this Wake: run \`${buildSpaceActionCommand(
      input,
      'reply "message"'
    )}\` and wait for it to finish before ending.`;
  }
  if (publicationRequirement.allowedActions.includes("post_status")) {
    return `- Final action for this Wake: run \`${buildSpaceActionCommand(
      input,
      'status done "short completion update"'
    )}\` and wait for it to finish before ending.`;
  }
  if (publicationRequirement.allowedActions.includes("stay_silent")) {
    return `- Final action for this Wake: run \`${buildSpaceActionCommand(
      input,
      'silent --reason "reason"'
    )}\` and wait for it to finish before ending.`;
  }
  return `- Final action for this Wake: run \`${buildPromptSpaceActionCommand(
    input
  )}\` and use one trailing wakeloop-space-action block.`;
}

function buildConditionalSpaceActionCommandLines(
  input: Pick<DispatchPromptBootstrapInput, "cliEntry">,
  publicationRequirement: ReturnType<typeof resolveSpacePublicationRequirement>
): string[] {
  const lines: string[] = [];
  if (publicationRequirement.allowedActions.includes("post_message")) {
    lines.push(
      "- Other action forms only when this Wake requires them:",
      `  Handoff: \`${buildSpaceActionCommand(
        input,
        'wake "@Agent Name" "summary and request"'
      )}\``,
      `  Reply + handoff: \`${buildSpaceActionCommand(
        input,
        'reply --wake "@Agent Name" "message"'
      )}\``
    );
  }
  if (publicationRequirement.allowedActions.includes("post_status")) {
    if (lines.length === 0) {
      lines.push("- Other action forms only when this Wake requires them:");
    }
    lines.push(
      `  Status: \`${buildSpaceActionCommand(
        input,
        'status done "short update"'
      )}\``
    );
  }
  if (publicationRequirement.allowedActions.includes("stay_silent")) {
    if (lines.length === 0) {
      lines.push("- Other action forms only when this Wake requires them:");
    }
    lines.push(
      `  Silent: \`${buildSpaceActionCommand(
        input,
        'silent --reason "reason"'
      )}\``
    );
  }
  lines.push(
    `  Targets: \`${buildSpaceActionCommand(input, "targets")}\``,
    `  Help: \`${buildSpaceActionCommand(input, "help")}\``
  );
  return lines;
}

function buildSpaceActionCommand(
  input: Pick<DispatchPromptBootstrapInput, "cliEntry">,
  action: string
): string {
  const cliEntry = normalizeOptionalText(input.cliEntry) ?? "wakeloop";
  return `${cliEntry} space action ${action}`;
}

function buildPromptSpaceActionCommand(
  input: Pick<DispatchPromptBootstrapInput, "cliEntry">
): string {
  const cliEntry = normalizeOptionalText(input.cliEntry) ?? "wakeloop";
  return `${cliEntry} prompt space-action --raw`;
}

function buildSendTargetReferenceLines(
  input: Pick<DispatchPromptBootstrapInput, "sendTargetReferenceLines">
): string[] {
  if (
    !(
      input.sendTargetReferenceLines &&
      input.sendTargetReferenceLines.length > 0
    )
  ) {
    return [];
  }

  return [
    "- Send target reference:",
    ...input.sendTargetReferenceLines.map((line) => `  ${line}`),
  ];
}

function buildIfUnsureLine(input: {
  cliEntry?: string | null;
  spaceId: string;
}): string {
  return [
    `Need context? Run \`${buildSpaceHistoryCommand(input)}\`.`,
    `Need action help? Run \`${buildSpaceActionCommand(input, "help")}\` or \`${buildSpaceActionCommand(
      input,
      "targets"
    )}\`.`,
    `Fallback JSON is last resort: only if every Space Action command fails, run \`${buildPromptSpaceActionCommand(input)}\`.`,
    "Still unclear? Ask one short question.",
  ].join(" ");
}

function buildSpaceHistoryCommand(input: {
  cliEntry?: string | null;
  spaceId: string;
}): string {
  const cliEntry = normalizeOptionalText(input.cliEntry) ?? "wakeloop";
  const normalizedSpaceId = normalizeOptionalText(input.spaceId);
  return normalizedSpaceId
    ? `${cliEntry} space history ${normalizedSpaceId} --kind text --limit 20`
    : `${cliEntry} space history <space-id> --kind text --limit 20`;
}

function buildActivatedByLine(input: DispatchPromptBootstrapInput): string {
  const mentionSourceProfileName = normalizeOptionalText(
    input.mentionSourceProfileName
  );
  if (mentionSourceProfileName) {
    return mentionSourceProfileName;
  }
  const mentionSourceProfileId = normalizeOptionalText(
    input.mentionSourceProfileId
  );
  if (mentionSourceProfileId) {
    return mentionSourceProfileId;
  }
  return "system";
}

function buildDispatchTriggerLine(input: DispatchPromptBootstrapInput): string {
  if (input.dispatchTriggerSource === "listen_all") {
    return "listen_all: this Space dispatches public messages to active Agent Profiles.";
  }

  if (readRecipientExplicitlyAddressedByActivationTargets(input)) {
    const targetProfileId = normalizeText(input.targetProfileId) || "unknown";
    return `Wake: this message targeted your Agent Profile (${targetProfileId}).`;
  }

  return "Wake: this message was assigned to you.";
}

function readRecipientExplicitlyAddressedByActivationTargets(
  input: DispatchPromptBootstrapInput
): boolean {
  if (
    typeof input.recipientExplicitlyAddressedByActivationTargets === "boolean"
  ) {
    return input.recipientExplicitlyAddressedByActivationTargets;
  }
  return isRecipientExplicitlyAddressedByActivationTargets({
    sourceMessageEnvelope: input.sourceMessageEnvelope ?? null,
    targetProfileId: input.targetProfileId,
  });
}

function buildSourceMessageLine(prompt: string): string {
  return collapseWhitespace(prompt, 220);
}

function buildReplyTargetLine(input: {
  replyToSnapshot: DispatchPromptBootstrapInput["replyToSnapshot"];
  threadContext: DispatchPromptBootstrapInput["threadContext"];
}): string {
  if (input.threadContext) {
    return "Thread";
  }
  return collapseWhitespace(
    buildDispatchReplyContextSummary(input.replyToSnapshot).summary,
    160
  );
}

function renderContinuityLine(input: {
  messagesSinceThen: number;
  higherAuthorityUpdates: boolean;
}): string | null {
  const parts: string[] = [];
  if (input.messagesSinceThen > 0) {
    parts.push(
      `${String(input.messagesSinceThen)} new ${
        input.messagesSinceThen === 1 ? "message" : "messages"
      }`
    );
  }
  if (input.higherAuthorityUpdates) {
    parts.push("higher-authority updates: yes");
  }
  if (parts.length === 0) {
    return null;
  }
  return `- Continuity: ${parts.join("; ")}`;
}

function shouldRenderReplyTargetLine(replyTarget: string): boolean {
  return normalizeOptionalText(replyTarget) !== "the current source message";
}

function collapseWhitespace(value: string, maxLength: number): string {
  const normalized = normalizeText(value).replace(/\s+/gu, " ");
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength - 3).trimEnd()}...`;
}

export async function resolveBootstrapSpaceOwnerProfileId(input: {
  spaceId: string;
  targetProfileId: string;
}): Promise<string | null> {
  const spaceConfigResult = await readSpaceConfig({
    wakeLoopProfileId: input.targetProfileId,
    space: input.spaceId,
  });
  if (spaceConfigResult.kind === "missing") {
    return null;
  }
  if (isLocalSpaceStateCorruptResult(spaceConfigResult)) {
    throw toLocalSpaceStateIntegrityError({
      operation: "reading bootstrap space owner profile context",
      result: spaceConfigResult,
    });
  }
  const spaceFacts = projectSpaceFacts(spaceConfigResult.value);
  if (!spaceFacts.creator || typeof spaceFacts.creator !== "object") {
    return null;
  }
  const creator = spaceFacts.creator as {
    profile?: unknown;
    profileId?: unknown;
  };
  return (
    normalizeOptionalText(
      typeof creator.profile === "string" ? creator.profile : creator.profileId
    ) ?? null
  );
}

export async function resolveBootstrapTargetProfileContext(input: {
  spaceId: string;
  targetProfileId: string;
  preferredMentionLabel?: string | null;
  preferredProfileName?: string | null;
}): Promise<{
  targetProfileMentionLabel: string | null;
  targetProfileName: string | null;
  targetProfileRole: string | null;
}> {
  const preferredMentionLabel =
    normalizeOptionalText(input.preferredMentionLabel) ?? null;
  const preferredProfileName =
    normalizeOptionalText(input.preferredProfileName) ?? null;
  const targetSpaceConfigResult = await readSpaceConfig({
    wakeLoopProfileId: input.targetProfileId,
    space: input.spaceId,
  });
  if (isLocalSpaceStateCorruptResult(targetSpaceConfigResult)) {
    throw toLocalSpaceStateIntegrityError({
      operation: "reading bootstrap target profile context",
      result: targetSpaceConfigResult,
    });
  }
  const targetProfileCache =
    targetSpaceConfigResult.kind === "ok"
      ? projectSpaceProfileCache(targetSpaceConfigResult.value)
      : null;
  const spaceProfileName =
    normalizeOptionalText(targetProfileCache?.profileName) ?? null;
  const spaceMentionAlias =
    normalizeOptionalText(targetProfileCache?.mentionAlias) ?? null;
  const spaceRole = normalizeOptionalText(targetProfileCache?.role) ?? null;
  const targetProfileName = spaceProfileName ?? preferredProfileName;
  if (spaceProfileName || spaceMentionAlias) {
    return {
      targetProfileMentionLabel:
        preferredMentionLabel ??
        resolvePrimaryMentionLabel({
          mentionAlias: spaceMentionAlias,
          profileId: input.targetProfileId,
          profileName: targetProfileName,
        }),
      targetProfileName,
      targetProfileRole: spaceRole,
    };
  }

  return {
    targetProfileMentionLabel:
      preferredMentionLabel ??
      resolvePrimaryMentionLabel({
        mentionAlias: null,
        profileId: input.targetProfileId,
        profileName: targetProfileName,
      }),
    targetProfileName,
    targetProfileRole: spaceRole,
  };
}

export async function resolveDispatchMentionSourceContext(input: {
  spaceId: string;
  mentionSourceProfileId: string | null;
  preferredMentionLabel?: string | null;
}): Promise<{
  mentionSourceProfileName: string | null;
  mentionSourceMentionLabel: string | null;
}> {
  if (!input.mentionSourceProfileId) {
    return {
      mentionSourceProfileName: null,
      mentionSourceMentionLabel: null,
    };
  }

  const preferredMentionLabel =
    normalizeOptionalText(input.preferredMentionLabel) ?? null;

  const sourceSpaceConfigResult = await readSpaceConfig({
    wakeLoopProfileId: input.mentionSourceProfileId,
    space: input.spaceId,
  });
  if (isLocalSpaceStateCorruptResult(sourceSpaceConfigResult)) {
    throw toLocalSpaceStateIntegrityError({
      operation: "reading dispatch mention source context",
      result: sourceSpaceConfigResult,
    });
  }
  const sourceProfileCache =
    sourceSpaceConfigResult.kind === "ok"
      ? projectSpaceProfileCache(sourceSpaceConfigResult.value)
      : null;
  const sourceProfileHandle =
    normalizeOptionalText(sourceProfileCache?.profileHandle) ?? null;
  const sourceMentionAlias =
    normalizeOptionalText(sourceProfileCache?.mentionAlias) ?? null;
  if (
    sourceMentionAlias ||
    sourceProfileCache?.profileName ||
    sourceProfileHandle
  ) {
    return {
      mentionSourceProfileName:
        normalizeOptionalText(sourceProfileCache?.profileName) ?? null,
      mentionSourceMentionLabel:
        preferredMentionLabel ??
        resolvePrimaryMentionLabel({
          mentionAlias: sourceMentionAlias,
          profileId: input.mentionSourceProfileId,
          profileName:
            normalizeOptionalText(sourceProfileCache?.profileName) ?? null,
        }),
    };
  }
  return {
    mentionSourceProfileName: null,
    mentionSourceMentionLabel:
      preferredMentionLabel ??
      resolvePrimaryMentionLabel({
        mentionAlias: null,
        profileId: input.mentionSourceProfileId,
        profileName: null,
      }),
  };
}

function resolvePrimaryMentionLabel(input: {
  mentionAlias: string | null;
  profileId: string;
  profileName: string | null;
}): string {
  return resolveSingleSpaceMentionLabels({
    mentionAlias: input.mentionAlias,
    profileId: input.profileId,
    profileName: input.profileName,
  }).primaryMentionLabel;
}

export function buildDispatchCorrelation(input: {
  deliverRequest: RuntimeTransportDeliverDispatchRequest;
  parsedTask: ParsedDispatchTask | null;
}): DispatchCorrelation {
  const payload = input.deliverRequest.payload;
  return {
    attemptId:
      input.parsedTask?.attemptId ??
      normalizeOptionalText(payload.attemptId) ??
      normalizeOptionalText(input.deliverRequest.attemptId) ??
      "unknown-attempt",
    agentControllerRef:
      input.parsedTask?.agentControllerRef ??
      normalizeOptionalText(payload.controllerSnapshot.agentControllerRef),
    dispatchId:
      input.parsedTask?.dispatchId ??
      normalizeOptionalText(payload.dispatchId) ??
      input.deliverRequest.dispatchId,
    sourceEventId:
      input.parsedTask?.sourceEventId ??
      normalizeOptionalText(payload.sourceEventId) ??
      "unknown-source-event",
    targetProfileId:
      input.parsedTask?.targetProfileId ??
      normalizeOptionalText(payload.targetProfileId) ??
      input.deliverRequest.profileId,
    taskId:
      input.parsedTask?.taskId ??
      normalizeOptionalText(payload.taskId) ??
      "unknown-task",
  };
}
